import { useEffect, useState } from 'react';
import { apiService } from '../services/api.service';

const DELINQUENCY_TYPES = [
  { value: 'EMPRESA', label: 'Empresa (todas sucursales)' },
  { value: 'SUC', label: 'Sucursal' },
  { value: 'SEMESTRAL_EM', label: 'Semestral empresa' },
  { value: 'SEMESTRAL_SUC', label: 'Semestral sucursal' },
];

function defaultDateRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    from: start.toISOString().slice(0, 10),
    to: now.toISOString().slice(0, 10),
  };
}

export function OperationalReports() {
  const range = defaultDateRange();
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [branchId, setBranchId] = useState('');
  const [delinquencyType, setDelinquencyType] = useState('EMPRESA');
  const [year, setYear] = useState(new Date().getFullYear());
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [delinquency, setDelinquency] = useState<any | null>(null);
  const [salesByPlan, setSalesByPlan] = useState<any[]>([]);
  const [salesByUser, setSalesByUser] = useState<any | null>(null);
  const [monthlyFinanced, setMonthlyFinanced] = useState<any | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);

  useEffect(() => {
    apiService.getBranches(true).then(setBranches).catch(() => {});
  }, []);

  const needsBranch = delinquencyType.includes('SUC');

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const branch = branchId || undefined;
      const [del, plan, user, monthly] = await Promise.all([
        apiService.getDelinquencyReport({ from, to, type: delinquencyType, branchId: needsBranch ? branch : undefined }),
        apiService.getSalesByPlanReport({ from, to, branchId: branch }),
        apiService.getSalesByUserReport({ from, to, branchId: branch }),
        apiService.getMonthlyFinancedReport({ year, branchId: branch }),
      ]);
      setDelinquency(del);
      setSalesByPlan(plan);
      setSalesByUser(user);
      setMonthlyFinanced(monthly);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (report: string) => {
    setExporting(report);
    try {
      await apiService.downloadOperationalReportXlsx({
        report,
        from,
        to,
        year,
        branchId: branchId || undefined,
        type: delinquencyType,
      });
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Reportes operativos</h1>
        <p className="text-sm text-gray-500 mt-1">Morosidad, ventas por plan/usuario y financiado mensual</p>
      </div>

      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-sm text-gray-600 mb-1">Desde</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="px-3 py-2 border rounded-md" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Hasta</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="px-3 py-2 border rounded-md" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Sucursal</label>
          <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className="px-3 py-2 border rounded-md min-w-[180px]">
            <option value="">Todas</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Tipo morosidad</label>
          <select value={delinquencyType} onChange={(e) => setDelinquencyType(e.target.value)} className="px-3 py-2 border rounded-md">
            {DELINQUENCY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Año financiado</label>
          <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className="px-3 py-2 border rounded-md w-24" />
        </div>
        <button
          onClick={loadAll}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Cargando…' : 'Generar reportes'}
        </button>
      </div>

      {/* Morosidad */}
      <section className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Morosidad</h2>
          <button
            disabled={exporting === 'delinquency'}
            onClick={() => exportReport('delinquency')}
            className="text-sm text-blue-600 hover:underline disabled:opacity-50"
          >
            {exporting === 'delinquency' ? 'Exportando…' : 'Exportar Excel'}
          </button>
        </div>
        {delinquency ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="p-3 bg-gray-50 rounded">
              <p className="text-gray-500">% Morosidad</p>
              <p className="text-2xl font-bold text-red-600">{delinquency.delinquencyPercent}%</p>
            </div>
            <div className="p-3 bg-gray-50 rounded">
              <p className="text-gray-500">Monto vencido</p>
              <p className="text-xl font-semibold">${delinquency.overdueAmount?.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded">
              <p className="text-gray-500">Cuotas vencidas</p>
              <p className="text-xl font-semibold">{delinquency.overdueCount}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded">
              <p className="text-gray-500">Cartera en análisis</p>
              <p className="text-xl font-semibold">${delinquency.totalPortfolioAmount?.toLocaleString()}</p>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">Ejecutá &quot;Generar reportes&quot; para ver datos.</p>
        )}
      </section>

      {/* Ventas por plan */}
      <section className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Ventas por plan</h2>
          <button
            disabled={exporting === 'sales-by-plan'}
            onClick={() => exportReport('sales-by-plan')}
            className="text-sm text-blue-600 hover:underline disabled:opacity-50"
          >
            Exportar Excel
          </button>
        </div>
        {salesByPlan.length > 0 ? (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2">Plan</th>
                <th className="py-2">Tipo</th>
                <th className="py-2 text-right">Cantidad</th>
                <th className="py-2 text-right">Monto</th>
              </tr>
            </thead>
            <tbody>
              {salesByPlan.map((r, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-2 font-mono">{r.planCode}</td>
                  <td className="py-2">{r.planType}</td>
                  <td className="py-2 text-right">{r.count}</td>
                  <td className="py-2 text-right">${r.totalAmount?.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-500 text-sm">Sin datos en el período.</p>
        )}
      </section>

      {/* Ventas por usuario */}
      <section className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Por usuario</h2>
          <button
            disabled={exporting === 'sales-by-user'}
            onClick={() => exportReport('sales-by-user')}
            className="text-sm text-blue-600 hover:underline disabled:opacity-50"
          >
            Exportar Excel
          </button>
        </div>
        {salesByUser ? (
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Cobranza (comprobantes)</h3>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-gray-500 border-b">
                    <th className="py-1">Usuario</th>
                    <th className="py-1 text-right">Comp.</th>
                    <th className="py-1 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {salesByUser.collectionsByUser?.map((r: any) => (
                    <tr key={r.userId} className="border-b border-gray-100">
                      <td className="py-1">{r.userName}</td>
                      <td className="py-1 text-right">{r.voucherCount}</td>
                      <td className="py-1 text-right">${r.collectionAmount?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Ventas (presolicitudes convertidas)</h3>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-gray-500 border-b">
                    <th className="py-1">Usuario</th>
                    <th className="py-1 text-right">Créditos</th>
                    <th className="py-1 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {salesByUser.loanSalesByUser?.map((r: any) => (
                    <tr key={r.userId} className="border-b border-gray-100">
                      <td className="py-1">{r.userName}</td>
                      <td className="py-1 text-right">{r.loansConverted}</td>
                      <td className="py-1 text-right">${r.salesAmount?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">Sin datos.</p>
        )}
      </section>

      {/* Financiado mensual */}
      <section className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Financiado mensual ({year})</h2>
          <button
            disabled={exporting === 'monthly-financed'}
            onClick={() => exportReport('monthly-financed')}
            className="text-sm text-blue-600 hover:underline disabled:opacity-50"
          >
            Exportar Excel
          </button>
        </div>
        {monthlyFinanced?.months ? (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-gray-500 border-b">
                <th className="py-2">Mes</th>
                <th className="py-2 text-right">Préstamos</th>
                <th className="py-2 text-right">Total financiado</th>
              </tr>
            </thead>
            <tbody>
              {monthlyFinanced.months.map((m: any) => (
                <tr key={m.month} className="border-b border-gray-100">
                  <td className="py-2">{m.month}</td>
                  <td className="py-2 text-right">{m.loanCount}</td>
                  <td className="py-2 text-right">${m.totalFinanced?.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-500 text-sm">Sin datos.</p>
        )}
      </section>
    </div>
  );
}
